import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LOGO_URL from "../../assets/Logo.png";

const links = [
  { id: "home", label: "Home" },
  { id: "services", label: "Services" },
  { id: "about", label: "About Us" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" },
];

const DataviquMark = () => (
  <img
    src={LOGO_URL}
    alt="Dataviqu"
    className="h-9 md:h-10 w-auto object-contain"
    draggable={false}
  />
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);

    handleScroll();

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setOpen(false);

    // If we're not on home, go there first
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }

    const element = document.getElementById(id);

    if (!element) return;

    if (window.__lenis) {
      window.__lenis.scrollTo(element, {
        offset: -64,
        duration: 1,
      });
    } else {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const NavItem = ({ id, label }) => {
    if (location.pathname === "/") {
      return (
        <button
          onClick={() => scrollToSection(id)}
          className="text-sm text-zinc-300 hover:text-white px-4 py-2 rounded-full hover:bg-white/5 transition-colors"
        >
          {label}
        </button>
      );
    }

    return (
      <Link
        to={`/#${id}`}
        className="text-sm text-zinc-300 hover:text-white px-4 py-2 rounded-full hover:bg-white/5 transition-colors"
      >
        {label}
      </Link>
    );
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 ${
        scrolled ? "glass-nav" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">

        {/* Logo */}
        {location.pathname === "/" ? (
          <button
            onClick={() => scrollToSection("home")}
            className="cursor-pointer"
          >
            <DataviquMark />
          </button>
        ) : (
          <Link to="/#home">
            <DataviquMark />
          </Link>
        )}

        {/* Desktop */}
        <nav className="hidden md:flex items-center gap-1">
          {links.map((link) => (
            <NavItem
              key={link.id}
              id={link.id}
              label={link.label}
            />
          ))}
        </nav>

        {/* CTA */}
        {location.pathname === "/" ? (
          <button
            onClick={() => scrollToSection("contact")}
            className="hidden md:inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors"
          >
            Start a project →
          </button>
        ) : (
          <Link
            to="/#contact"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors"
          >
            Start a project →
          </Link>
        )}

        {/* Mobile Toggle */}
        <button
          className="md:hidden p-2"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass-nav border-t border-white/5"
          >
            <div className="px-6 py-6 flex flex-col gap-2">

              {links.map((link) =>
                location.pathname === "/" ? (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="text-left py-3 border-b border-white/5 text-zinc-200"
                  >
                    {link.label}
                  </button>
                ) : (
                  <Link
                    key={link.id}
                    to={`/#${link.id}`}
                    onClick={() => setOpen(false)}
                    className="text-left py-3 border-b border-white/5 text-zinc-200"
                  >
                    {link.label}
                  </Link>
                )
              )}

              {location.pathname === "/" ? (
                <button
                  onClick={() => scrollToSection("contact")}
                  className="mt-4 px-5 py-3 rounded-full bg-white text-black"
                >
                  Start a project →
                </button>
              ) : (
                <Link
                  to="/#contact"
                  onClick={() => setOpen(false)}
                  className="mt-4 px-5 py-3 rounded-full bg-white text-black text-center"
                >
                  Start a project →
                </Link>
              )}

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}